import { useMemo, useState } from "react";
import { QueryKeys } from "~/application/constants";
import { AGENCY_CUSTOMER_ID } from "~/application/constants/storageKeys";
import { travelAnalyticsService } from "~/application/usecases/TravelAnalytics";
import { useAuth } from "~/presentation/core/contexts/AuthContext";
import { DashboardBIV2Container } from "./DashboardBIV2Container";
import { useCostCenterAnalytics } from "./hooks/useCostCenterAnalytics";
import { useDashboardQuery } from "./hooks/useDashboardQuery";
import { useProjectAnalytics } from "./hooks/useProjectAnalytics";
import { TravelAnalyticsPageFilters } from "./types";
import { currentYearMonth, startOfYearYearMonth } from "./utils";

const LOG_TAG = "Customer/CustomerDashboardBIV2Page/DashboardBIV2Page";

export const DashboardBIV2Page = () => {
  const { user } = useAuth();

  const customerUuid = user?.customer?.uuid || (localStorage.getItem(AGENCY_CUSTOMER_ID) as string) || "";

  const [filters, setFilters] = useState<TravelAnalyticsPageFilters>({
    customerUuid,
    fromYearMonth: startOfYearYearMonth(),
    toYearMonth: currentYearMonth(),
    productType: undefined,
    compareWithPrevious: false,
  });

  const queryFilters = useMemo(() => ({ ...filters, customerUuid }), [filters, customerUuid]);
  const enabled = !!customerUuid;

  const { data: summary, isFetching: isFetchingSummary } = useDashboardQuery(
    [QueryKeys.TA_SUMMARY, queryFilters],
    () => travelAnalyticsService.getSummary(queryFilters),
    enabled
  );

  const { data: totalSpent, isFetching: isFetchingTotalSpent } = useDashboardQuery(
    [QueryKeys.TA_TOTAL_SPENT, queryFilters],
    () => travelAnalyticsService.getTotalSpent(queryFilters),
    enabled
  );

  const { data: byProduct, isFetching: isFetchingByProduct } = useDashboardQuery(
    [QueryKeys.TA_BY_PRODUCT, queryFilters],
    () => travelAnalyticsService.getByProduct(queryFilters),
    enabled
  );

  const {
    data: byCostCenter,
    isFetching: isFetchingByCostCenter,
    page: costCenterPage,
    setPage: setCostCenterPage,
    search: costCenterSearch,
    setSearch: setCostCenterSearch,
  } = useCostCenterAnalytics({ queryFilters });

  const {
    data: byProject,
    isFetching: isFetchingByProject,
    page: projectPage,
    setPage: setProjectPage,
    search: projectSearch,
    setSearch: setProjectSearch,
  } = useProjectAnalytics({ queryFilters });

  const { data: byUnit, isFetching: isFetchingByUnit } = useDashboardQuery(
    [QueryKeys.TA_BY_UNIT, queryFilters],
    () => travelAnalyticsService.getByUnit(queryFilters),
    enabled
  );

  const { data: policyCompliance, isFetching: isFetchingPolicyCompliance } = useDashboardQuery(
    [QueryKeys.TA_POLICY_COMPLIANCE, queryFilters],
    () => travelAnalyticsService.getPolicyCompliance(queryFilters),
    enabled
  );

  const { data: policyByUnit, isFetching: isFetchingPolicyByUnit } = useDashboardQuery(
    [QueryKeys.TA_POLICY_BY_UNIT, queryFilters],
    () => travelAnalyticsService.getPolicyByUnit(queryFilters),
    enabled
  );

  const { data: topViolationsCostCenter, isFetching: isFetchingTopViolationsCostCenter } = useDashboardQuery(
    [QueryKeys.TA_TOP_VIOLATIONS, "cost-center", queryFilters],
    () =>
      travelAnalyticsService.getTopViolations({
        ...queryFilters,
        dimension: "cost-center",
        limit: 30,
      }),
    enabled
  );

  const { data: topViolationsTraveler, isFetching: isFetchingTopViolationsTraveler } = useDashboardQuery(
    [QueryKeys.TA_TOP_VIOLATIONS, "traveler", queryFilters],
    () =>
      travelAnalyticsService.getTopViolations({
        ...queryFilters,
        dimension: "traveler",
        limit: 30,
      }),
    enabled
  );

  const { data: topViolationsRequester, isFetching: isFetchingTopViolationsRequester } = useDashboardQuery(
    [QueryKeys.TA_TOP_VIOLATIONS, "requester", queryFilters],
    () =>
      travelAnalyticsService.getTopViolations({
        ...queryFilters,
        dimension: "requester",
        limit: 30,
      }),
    enabled
  );

  const { data: topViolationsApprover, isFetching: isFetchingTopViolationsApprover } = useDashboardQuery(
    [QueryKeys.TA_TOP_VIOLATIONS, "approver", queryFilters],
    () =>
      travelAnalyticsService.getTopViolations({
        ...queryFilters,
        dimension: "approver",
        limit: 30,
      }),
    enabled
  );

  const { data: advanceByReason, isFetching: isFetchingAdvanceByReason } = useDashboardQuery(
    [QueryKeys.TA_ADVANCE_BY_REASON, queryFilters],
    () => travelAnalyticsService.getAdvanceByReason(queryFilters),
    enabled
  );

  const { data: advanceByTraveler, isFetching: isFetchingAdvanceByTraveler } = useDashboardQuery(
    [QueryKeys.TA_ADVANCE_BY_TRAVELER, queryFilters],
    () => travelAnalyticsService.getAdvanceByTraveler({ ...queryFilters, limit: 30 }),
    enabled
  );

  return (
    <DashboardBIV2Container
      filters={filters}
      onChangeFilters={setFilters}
      summary={summary}
      isFetchingSummary={isFetchingSummary}
      policyCompliance={policyCompliance}
      isFetchingPolicyCompliance={isFetchingPolicyCompliance}
      totalSpent={totalSpent}
      isFetchingTotalSpent={isFetchingTotalSpent}
      byProduct={byProduct}
      isFetchingByProduct={isFetchingByProduct}
      byUnit={byUnit}
      isFetchingByUnit={isFetchingByUnit}
      policyByUnit={policyByUnit}
      isFetchingPolicyByUnit={isFetchingPolicyByUnit}
      byCostCenter={byCostCenter}
      isFetchingByCostCenter={isFetchingByCostCenter}
      byProject={byProject}
      isFetchingByProject={isFetchingByProject}
      costCenterPage={costCenterPage}
      onCostCenterPageChange={setCostCenterPage}
      costCenterSearch={costCenterSearch}
      onCostCenterSearchChange={setCostCenterSearch}
      projectPage={projectPage}
      onProjectPageChange={setProjectPage}
      projectSearch={projectSearch}
      onProjectSearchChange={setProjectSearch}
      topViolationsCostCenter={topViolationsCostCenter}
      isFetchingTopViolationsCostCenter={isFetchingTopViolationsCostCenter}
      topViolationsTraveler={topViolationsTraveler}
      isFetchingTopViolationsTraveler={isFetchingTopViolationsTraveler}
      topViolationsRequester={topViolationsRequester}
      isFetchingTopViolationsRequester={isFetchingTopViolationsRequester}
      topViolationsApprover={topViolationsApprover}
      isFetchingTopViolationsApprover={isFetchingTopViolationsApprover}
      advanceByReason={advanceByReason}
      isFetchingAdvanceByReason={isFetchingAdvanceByReason}
      advanceByTraveler={advanceByTraveler}
      isFetchingAdvanceByTraveler={isFetchingAdvanceByTraveler}
    />
  );
};
